/**
 * Product Discounts Module
 *
 * Shows the label-based discount on product pages and listing cards while the
 * configured schedule window is open (see discount-config.js).
 *
 * - Which products / what percent: read live from the Shopify product labels
 *   `label__10%:primary` / `label__30%:primary` (via the shared catalog cache).
 * - Display: struck-through original price + discounted price + a "-XX %" badge.
 * - The actual discount is applied by Shopify's automatic discount at checkout;
 *   this module only mirrors the same percent for display.
 *
 * Progressive enhancement: the static HTML paints the normal price, then this
 * overlays the sale once the catalog data resolves (mirrors the stock badges).
 */

import { getProductTags } from "./stock-cache.js";
import { isDiscountActive } from "./discount-config.js";

const LABEL_PERCENT_RE = /label__(\d+)%/;

/** Highest discount percent encoded in a product's tags, or 0 if none. */
function discountPercentFromTags(tags) {
  let pct = 0;
  for (const tag of tags || []) {
    const m = LABEL_PERCENT_RE.exec(tag);
    if (m) {
      pct = Math.max(pct, parseInt(m[1], 10));
    }
  }
  return pct > 0 && pct < 100 ? pct : 0;
}

/** Parse a de-DE price string ("1.990,00", "29,90 €") to a number. */
function parsePrice(str) {
  const cleaned = String(str)
    .replace(/[^\d.,]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** Format a number as a de-DE price string with two decimals ("1.791,00"). */
function formatPrice(n) {
  return n.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Apply a percent discount, rounded to the cent (matches Shopify per-line). */
function applyDiscount(n, pct) {
  return Math.round(n * (1 - pct / 100) * 100) / 100;
}

export async function init() {
  if (!isDiscountActive()) {
    return;
  }

  const tags = await getProductTags();
  if (!tags) {
    return;
  }

  updateProductPage(tags);
  updateListingCards(tags);
}

function updateProductPage(tags) {
  const product = document.querySelector("[data-shopify-handle]");
  if (!product) {
    return;
  }

  const pct = discountPercentFromTags(tags[product.dataset.shopifyHandle]);
  if (!pct) {
    return;
  }

  const priceEl = document.querySelector("[data-price]");
  const priceRow = document.querySelector(".shop-product__price-row");
  if (!priceEl || !priceRow || priceEl.dataset.discountApplied) {
    return;
  }
  priceEl.dataset.discountApplied = "1";

  const priceBox = priceEl.closest(".shop-product__price") || priceEl.parentElement;

  // Render the current (first) variant's sale price.
  renderDetailPrice(priceRow, priceBox, priceEl, priceEl.textContent, pct);

  // Badge (once per row).
  const badge = document.createElement("span");
  badge.className = "shop-product__discount-badge";
  badge.textContent = `-${pct}%`;
  priceRow.appendChild(badge);

  // Keep the sticky mobile bar's price in sync on first paint.
  const stickyPrice = document.querySelector("[data-sticky-price]");
  if (stickyPrice) {
    const num = parsePrice(priceEl.dataset.original);
    if (num !== null) {
      stickyPrice.textContent = formatPrice(applyDiscount(num, pct));
    }
  }

  // Update on variant change. shop.js sets the raw price then dispatches this
  // event; this listener (registered after shop.js in bootstrap) overrides the
  // displayed value with the discounted one and refreshes the struck original.
  const variants = document.querySelector("[data-variants]");
  if (variants) {
    variants.addEventListener("variantchange", (e) => {
      renderDetailPrice(priceRow, priceBox, priceEl, e.detail.price, pct);
      const sticky = document.querySelector("[data-sticky-price]");
      if (sticky) {
        const num = parsePrice(e.detail.price);
        if (num !== null) {
          sticky.textContent = formatPrice(applyDiscount(num, pct));
        }
      }
    });
  }
}

function renderDetailPrice(priceRow, priceBox, priceEl, rawPriceText, pct) {
  const num = parsePrice(rawPriceText);
  if (num === null) {
    return;
  }
  priceEl.dataset.original = formatPrice(num);
  priceEl.textContent = formatPrice(applyDiscount(num, pct));
  priceBox.classList.add("shop-product__price--sale");

  let original = priceRow.querySelector(".shop-product__price-original");
  if (!original) {
    original = document.createElement("span");
    original.className = "shop-product__price-original";
    priceRow.insertBefore(original, priceBox);
  }
  original.textContent = `${formatPrice(num)} €`;
}

function updateListingCards(tags) {
  const cards = document.querySelectorAll("[data-product-handle]");
  cards.forEach((card) => {
    const pct = discountPercentFromTags(tags[card.dataset.productHandle]);
    if (!pct) {
      return;
    }

    const priceEl = card.querySelector(".shop-card__price");
    if (!priceEl || priceEl.dataset.discountApplied) {
      return;
    }

    const num = parsePrice(priceEl.textContent);
    if (num === null) {
      return;
    }
    priceEl.dataset.discountApplied = "1";

    const original = priceEl.textContent.trim();
    // Keep any leading label ("ab " / "from ") outside the strikethrough — it is
    // not part of the price. It leads both the struck original and the sale price.
    const prefix = original.match(/^\D*/)[0];
    const amount = original.slice(prefix.length);
    priceEl.innerHTML =
      `${prefix}` +
      `<span class="shop-card__price-original">${amount}</span> ` +
      `<span class="shop-card__price-discounted">${formatPrice(applyDiscount(num, pct))} &euro;</span>`;

    if (!card.querySelector(".shop-card__discount-badge")) {
      const badge = document.createElement("span");
      badge.className = "shop-card__discount-badge";
      badge.textContent = `-${pct}%`;
      card.insertBefore(badge, card.firstChild);
    }
  });
}
